import { useState, useEffect } from "react";
import { API } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { BookOpen, CheckCircle, XCircle, Clock, ShoppingBag, User, Phone, Mail, Package, Trash2, Camera, Loader2, Wallet } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const etatLabels = { neuf: "Neuf", bon_etat: "Bon état", usage: "Usagé" };

const statutConfig = {
  en_attente: { label: "En attente", color: "bg-yellow-100 text-yellow-800" },
  accepte: { label: "Accepté - À déposer", color: "bg-green-100 text-green-800" },
  en_vente: { label: "En vente", color: "bg-blue-100 text-blue-800" },
  vendu: { label: "Vendu", color: "bg-emerald-100 text-emerald-800" },
  refuse: { label: "Refusé", color: "bg-red-100 text-red-800" },
  expire: { label: "Expiré", color: "bg-gray-100 text-gray-800" },
};

const BookProposalsPage = () => {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [actionId, setActionId] = useState(null);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [prixVente, setPrixVente] = useState("");
  const [prixRachat, setPrixRachat] = useState("");
  const [motifRefus, setMotifRefus] = useState("");

  const fetchProposals = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== "all") params.statut = filter;
      const res = await API.get("/book-proposals", { params });
      setProposals(res.data);
    } catch {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProposals(); }, [filter]);

  const handleAccept = async (proposalId) => {
    if (!prixVente || parseFloat(prixVente) <= 0) {
      toast.error("Le prix de vente est obligatoire");
      return;
    }
    try {
      await API.put(`/book-proposals/${proposalId}`, {
        statut: "accepte",
        prix_vente: parseFloat(prixVente),
        prix_rachat: prixRachat ? parseFloat(prixRachat) : 0
      });
      toast.success("Proposition acceptée ! Le client a 7 jours pour déposer le livre.");
      setActionId(null);
      setPrixVente("");
      setPrixRachat("");
      fetchProposals();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur");
    }
  };

  const handleReceive = async (proposalId) => {
    try {
      const res = await API.put(`/book-proposals/${proposalId}`, { statut: "receptionne" });
      const creditMontant = res.data?.credit_montant;
      const soldeCredit = res.data?.solde_credit_client;
      if (creditMontant) {
        toast.success(`Livre réceptionné ! Crédit de ${creditMontant.toFixed(2)}€ généré. Solde client : ${(soldeCredit || 0).toFixed(2)}€`);
      } else {
        toast.success("Livre réceptionné et mis en vente dans la boutique !");
      }
      fetchProposals();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur");
    }
  };

  const handleExpire = async (proposalId) => {
    try {
      await API.put(`/book-proposals/${proposalId}`, { statut: "expire" });
      toast.success("Proposition marquée comme expirée");
      fetchProposals();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleDelete = async (proposalId) => {
    if (!window.confirm("Supprimer cette proposition ? Le produit associé sera aussi supprimé.")) return;
    try {
      await API.delete(`/book-proposals/${proposalId}`);
      toast.success("Proposition supprimée");
      fetchProposals();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const adminUploadPhotos = async (proposalId, files) => {
    setUploadingFor(proposalId);
    try {
      const formData = new FormData();
      for (const file of files) {
        formData.append("files", file);
      }
      await API.post(`/book-proposals/${proposalId}/photos`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Photo(s) ajoutée(s)");
      fetchProposals();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur upload");
    } finally {
      setUploadingFor(null);
    }
  };

  const handleRefuse = async (proposalId) => {
    try {
      await API.put(`/book-proposals/${proposalId}`, {
        statut: "refuse",
        motif_refus: motifRefus
      });
      toast.success("Proposition refusée");
      setActionId(null);
      setMotifRefus("");
      fetchProposals();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const pending = proposals.filter(p => p.statut === "en_attente").length;
  const accepted = proposals.filter(p => p.statut === "accepte").length;
  const enVente = proposals.filter(p => p.statut === "en_vente").length;

  return (
    <div className="space-y-6" data-testid="book-proposals-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <BookOpen className="h-7 w-7" />
          Propositions de livres
        </h1>
        <p className="text-muted-foreground">
          {pending > 0 && <span className="text-yellow-600 font-medium">{pending} en attente</span>}
          {pending > 0 && accepted > 0 && " · "}
          {accepted > 0 && <span className="text-green-600 font-medium">{accepted} à réceptionner</span>}
          {(pending > 0 || accepted > 0) && enVente > 0 && " · "}
          {enVente > 0 && <span className="text-blue-600">{enVente} en vente</span>}
          {!pending && !accepted && !enVente && "Aucune proposition"}
        </p>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 bg-muted p-1 rounded-lg w-fit overflow-x-auto">
        {[
          { key: "all", label: "Toutes" },
          { key: "en_attente", label: "En attente" },
          { key: "accepte", label: "À déposer" },
          { key: "en_vente", label: "En vente" },
          { key: "refuse", label: "Refusées" },
          { key: "expire", label: "Expirées" },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              filter === tab.key ? "bg-background shadow font-medium" : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`filter-${tab.key}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Proposals list */}
      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : proposals.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <BookOpen className="h-12 w-12 text-muted-foreground/30 mb-4" />
            <p className="font-medium">Aucune proposition</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {proposals.map((p) => {
            const config = statutConfig[p.statut] || statutConfig.en_attente;
            const isExpanded = actionId === p.id;
            return (
              <Card key={p.id} className={p.statut === "en_attente" ? "border-yellow-200 bg-yellow-50/30" : ""} data-testid={`proposal-${p.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-lg">{p.titre}</h3>
                        <Badge className={config.color}>{config.label}</Badge>
                        <Badge variant="outline">{etatLabels[p.etat] || p.etat}</Badge>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-sm text-muted-foreground">
                        {p.auteur && <span>{p.auteur}</span>}
                        {p.matiere_classe && <span className="font-medium">{p.matiere_classe}</span>}
                      </div>
                      {p.description && <p className="text-sm text-muted-foreground mt-1">{p.description}</p>}
                      
                      {/* Client info */}
                      <div className="flex flex-wrap gap-4 mt-3 text-sm">
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <User className="h-3.5 w-3.5" /> {p.client_nom}
                        </span>
                        {p.client_telephone && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Phone className="h-3.5 w-3.5" /> {p.client_telephone}
                          </span>
                        )}
                        {p.client_email && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <Mail className="h-3.5 w-3.5" /> {p.client_email}
                          </span>
                        )}
                      </div>

                      {/* Prices */}
                      <div className="flex flex-wrap gap-4 mt-2 text-sm">
                        {p.prix_souhaite > 0 ? (
                          <span>Prix souhaité : <strong>{p.prix_souhaite.toFixed(2)} EUR</strong></span>
                        ) : (
                          <span className="text-emerald-600 font-medium">Livre offert par le client</span>
                        )}
                        {p.prix_vente > 0 && (
                          <span className="text-primary font-medium">Prix de vente : {p.prix_vente.toFixed(2)} EUR</span>
                        )}
                        {p.prix_rachat > 0 && (
                          <span className="text-green-600">Rachat : {p.prix_rachat.toFixed(2)} EUR</span>
                        )}
                      </div>
                      {p.credit_montant > 0 && (
                        <div className="mt-2 inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 text-sm">
                          <Wallet className="h-3.5 w-3.5 text-emerald-600" />
                          <span className="text-emerald-700">Crédit généré : <strong>{p.credit_montant.toFixed(2)} EUR</strong></span>
                          {p.solde_credit_client !== undefined && (
                            <span className="text-emerald-500 ml-2">Solde client : {p.solde_credit_client.toFixed(2)} EUR</span>
                          )}
                        </div>
                      )}

                      {/* Photos */}
                      <div className="flex flex-wrap gap-2 mt-2 items-center">
                        {(p.photos || []).map((photo, idx) => (
                          <img
                            key={idx}
                            src={`${BACKEND}${photo}`}
                            alt={`Photo ${idx + 1}`}
                            className="h-16 w-16 object-cover rounded-lg border"
                            onError={(e) => { e.target.style.display = "none"; }}
                          />
                        ))}
                        {(p.photos || []).length < 5 && (
                          <label className="h-16 w-16 flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-muted-foreground/30 cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-colors">
                            {uploadingFor === p.id ? (
                              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                            ) : (
                              <>
                                <Camera className="h-4 w-4 text-muted-foreground/50" />
                                <span className="text-[9px] text-muted-foreground mt-0.5">Photo</span>
                              </>
                            )}
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              multiple
                              onChange={(e) => {
                                if (e.target.files?.length) adminUploadPhotos(p.id, Array.from(e.target.files));
                                e.target.value = "";
                              }}
                              disabled={uploadingFor === p.id}
                            />
                          </label>
                        )}
                      </div>

                      <p className="text-xs text-muted-foreground mt-2">
                        Proposé le {new Date(p.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        {p.traite_par_nom && <span> · Traité par {p.traite_par_nom}</span>}
                        {p.date_limite_depot && p.statut === "accepte" && (
                          <span className="text-orange-600 font-medium"> · Dépôt avant le {new Date(p.date_limite_depot).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })}</span>
                        )}
                        {p.date_reception && <span> · Réceptionné le {new Date(p.date_reception).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" })}</span>}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2 shrink-0 items-end">
                      {p.statut === "en_attente" && !isExpanded && (
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => { setActionId(p.id); setPrixVente(p.prix_souhaite ? String(p.prix_souhaite) : ""); setPrixRachat(""); }} data-testid={`accept-btn-${p.id}`}>
                            <CheckCircle className="h-4 w-4 mr-1" /> Accepter
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => { setActionId(`refuse-${p.id}`); setMotifRefus(""); }} data-testid={`refuse-btn-${p.id}`}>
                            <XCircle className="h-4 w-4 mr-1" /> Refuser
                          </Button>
                        </div>
                      )}
                      {p.statut === "accepte" && (
                        <>
                          <Button size="sm" onClick={() => handleReceive(p.id)} data-testid={`receive-btn-${p.id}`}>
                            <Package className="h-4 w-4 mr-1" /> Livre réceptionné
                          </Button>
                          <Button size="sm" variant="ghost" className="text-muted-foreground text-xs" onClick={() => handleExpire(p.id)} data-testid={`expire-btn-${p.id}`}>
                            Marquer expiré
                          </Button>
                        </>
                      )}
                      <button
                        onClick={() => handleDelete(p.id)}
                        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-red-600 transition-colors mt-1"
                        data-testid={`delete-btn-${p.id}`}
                      >
                        <Trash2 className="h-3 w-3" /> Supprimer
                      </button>
                    </div>
                  </div>

                  {/* Accept form */}
                  {isExpanded && (
                    <div className="mt-4 p-4 rounded-lg border bg-green-50/50 space-y-3" data-testid={`accept-form-${p.id}`}>
                      <p className="font-medium text-sm">Fixer le prix de vente</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Prix de vente (EUR) *</Label>
                          <Input
                            type="number"
                            step="0.50"
                            min="0.50"
                            value={prixVente}
                            onChange={(e) => setPrixVente(e.target.value)}
                            placeholder="Ex: 12.00"
                            data-testid={`prix-vente-${p.id}`}
                            autoFocus
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Prix de rachat au client (EUR)</Label>
                          <Input
                            type="number"
                            step="0.50"
                            min="0"
                            value={prixRachat}
                            onChange={(e) => setPrixRachat(e.target.value)}
                            placeholder="Ex: 5.00"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleAccept(p.id)} data-testid={`confirm-accept-${p.id}`}>
                          Valider et mettre en vente
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setActionId(null)}>
                          Annuler
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Refuse form */}
                  {actionId === `refuse-${p.id}` && (
                    <div className="mt-4 p-4 rounded-lg border bg-red-50/50 space-y-3" data-testid={`refuse-form-${p.id}`}>
                      <div className="space-y-1">
                        <Label className="text-xs">Motif du refus (optionnel)</Label>
                        <Input
                          value={motifRefus}
                          onChange={(e) => setMotifRefus(e.target.value)}
                          placeholder="Ex: Livre trop abîmé, édition obsolète..."
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="destructive" onClick={() => handleRefuse(p.id)} data-testid={`confirm-refuse-${p.id}`}>
                          Confirmer le refus
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setActionId(null)}>
                          Annuler
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BookProposalsPage;
